import { Form, Input, Button, Row, Col, message } from "antd";
import Loader from "../../components/Loader";
import { Dispatch, SetStateAction, useState } from "react";
import { UserData } from "../../models/user";
import UploadUtils from "../../components/Upload";
import { IconArrowNarrowLeft, IconArrowNarrowRight } from "@tabler/icons-react";
import ProfileUploader from "../../components/ProfileUploader";
import { stringValidation } from "../../utils/ValidationRules";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useAPI } from "../../hooks/useAPI";
import { ENDPOINTS } from "../../constants/endpoints.constant";
import { updateCall } from "../../constants/apicalls.constant";
import { OnboardingType } from "../../models/onboarding";

import { useSelector } from "react-redux";
import { RootState } from "../../store/store";

export type UpdateUser = {
  status: boolean;
  message: string;
  data: OnboardingType;
  errors?: {
    [fieldName: string]: string[];
  };
};
const UserOnboardingForm = ({
  setStep,
  step = 1,
  response,
  firstname,
  setFirstName,
  myprofile,
  isProfileSetting = false,
}: {
  setStep?: Dispatch<SetStateAction<number>>;
  step?: number;
  response?: UpdateUser | null;
  myprofile?: boolean;
  firstname?: string | null | undefined;
  setFirstName?: Dispatch<SetStateAction<string | null | undefined>>;
  isProfileSetting?: boolean;
}) => {
  const user = useSelector((state: RootState) => state.user);

  const { loading, error, trigger } = useAPI<UpdateUser>(
    ENDPOINTS.update_user_profile,
  );
  const [uploadStatus, setUploadStatus] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [profilePic, setProfilePic] = useState<string | null>(null);

  const submitForm = async (values: UserData) => {
    values.dob = values?.dob ? dayjs(values?.dob).format("YYYY/MM/DD") : "";
    const originalFileList = values.profile_picture;
    if (values.profile_picture && profilePic) {
      values.profile_picture = UploadUtils?.processUploadImage(
        values.profile_picture,
      )?.[0]?.uid;
    } else {
      values.profile_picture = undefined;
    }
    const res = await trigger(
      updateCall<any>(response?.data?.user_details?.user?.id || user.id || "", {
        ...values,
        id: response?.data?.user_details?.user?.id || user.id,
        status: myprofile
          ? user?.status
          : user?.workspace?.role &&
            user?.workspace?.role !== "owner" &&
            user?.workspace?.role !== null
            ? "active"
            : "draft",
      }),
    );

    if (res?.status) {
      message.success("Profile Updated successfully");
      form.setFieldsValue({
        ...values,
        profile_picture: Array.isArray(originalFileList) ? originalFileList : [],
        dob: values.dob ? dayjs(values.dob) : null,
      });
      if (setFirstName) {
        setFirstName(values.first_name ?? "");
      }
      if (Array.isArray(originalFileList) && originalFileList[0]) {
        const pic: any = originalFileList[0];
        const url = pic.response?.[0]?.url ?? pic.url;
        setProfilePic(url ?? null);
      } else {
        setProfilePic(null);
      }
    } else {
      if (res?.errors) {
        const fieldErrors = Object.keys(res.errors).map((field) => ({
          name: field,
          errors: res.errors?.[field] || [],
        }));
        form.setFields(fieldErrors);
      }
      message.error(res?.message);
    }
  };
  useEffect(() => {
    if (response?.data?.user_details?.user) {
      const { user } = response.data.user_details;

      let profilePic = null;

      if (Array.isArray(user.profile_picture)) {
        profilePic = user.profile_picture[0];
      }

      form.setFieldsValue({
        ...user,
        profile_picture: profilePic
          ? [
            {
              uid: profilePic.uid,
              url: profilePic.url,
            },
          ]
          : [],
        dob: user.dob ? dayjs(user.dob) : null,
      });

      if (profilePic) {
        setProfilePic(profilePic.url);
      }
    } else if (user && myprofile) {
      let profilePic = null;

      if (Array.isArray(user.profile_picture)) {
        profilePic = user.profile_picture[0];
      } else if (typeof user.profile_picture === "string") {
        profilePic = { url: user.profile_picture, uid: "-1" };
      }

      form.setFieldsValue({
        ...user,
        profile_picture: profilePic
          ? [
            {
              uid: profilePic.uid,
              url: profilePic.url,
            },
          ]
          : [],
        dob: user.dob ? dayjs(user.dob) : null,
      });

      if (profilePic) {
        setProfilePic(profilePic.url);
      }
    }
  }, [response, user, myprofile]);

  useEffect(() => {
    if (!myprofile) {
      form.setFieldsValue({
        first_name: firstname ?? "",
      });
    }
  }, [firstname]);

  if (error) return <>something went wrong ......</>;
  return (
    <Loader spinning={loading}>
      <Form
        className={`${step === 1 ? "block" : "hidden"}`}
        form={form}
        onFinish={(values) => submitForm(values)}
        layout="vertical"
        style={{ marginTop: "30px" }}
      >
        <ProfileUploader
          name={"profile_picture"}
          image={profilePic}
          uploadStatus={uploadStatus}
          setImage={setProfilePic}
          setUploadStatus={setUploadStatus}
          label="Profile Picture"
        />
        <Row gutter={[24, 0]}>
          <Col xs={{ span: 12 }} sm={{ span: 12 }} md={{ span: 12 }}>
            <Form.Item<UserData>
              label="First Name"
              name={"first_name"}
              rules={stringValidation("First Name")}
            >
              <Input
                {...(setFirstName
                  ? {
                    value: firstname ?? "",
                    onChange: (e) => setFirstName(e.target.value),
                  }
                  : {})}
              />
            </Form.Item>
          </Col>
          <Col xs={{ span: 12 }} sm={{ span: 12 }} md={{ span: 12 }}>
            <Form.Item<UserData>
              label="Last Name"
              name={"last_name"}
              rules={stringValidation("Last Name", "", false)}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col
            xs={{ span: 24 }}
            sm={{ span: isProfileSetting ? 12 : 24 }}
            md={{ span: isProfileSetting ? 12 : 24 }}
          >
            <Form.Item<UserData>
              label="Email Address"
              name={"email"}
              rules={[
                { required: false, message: "Please enter your mail" },
                { type: "email", message: "Invalid Email Address" },
              ]}
            >
              <Input disabled />
            </Form.Item>
          </Col>
          {isProfileSetting ? (
            <Col
              xs={{ span: 24 }}
              sm={{ span: isProfileSetting ? 12 : 24 }}
              md={{ span: isProfileSetting ? 12 : 24 }}
            >
              <Form.Item<UserData> label="Phone Number" name={"phone"}>
                <Input prefix="+91" disabled />
              </Form.Item>
            </Col>
          ) : null}
          {/* <Col xs={{ span: 12 }} sm={{ span: 12 }} md={{ span: 12 }}>
          <Form.Item<UserData>
            label="Date of Birth"
            name={"dob"}
            rules={[
              { required: false, message: "Please select your date of birth" },
            ]}
          >
            <DatePicker
              disabledDate={disabledPastDate}
              className="w-full"
              placeholder=""
            />
          </Form.Item>
        </Col> */}
          <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }}>
            <div
              className={`flex  ${!user?.workspace?.role ||
                user?.workspace?.role === "owner" ||
                user?.workspace?.role === null
                ? "!justify-between"
                : "!justify-end"
                } `}
            >
              {!user?.workspace?.role ||
                user?.workspace?.role === "owner" ||
                user?.workspace?.role === null ? (
                <Button
                  onClick={() => {
                    if (setStep) {
                      setStep(0);
                    }
                  }}
                  disabled={uploadStatus || loading}
                  icon={<IconArrowNarrowLeft size={16} />}
                  className={`${myprofile ? "!hidden" : ""}`}
                >
                  Back
                </Button>
              ) : (
                <></>
              )}

              <Button
                type="primary"
                htmlType="submit"
                icon={myprofile ? <></> : <IconArrowNarrowRight size={16} />}
                iconPosition="end"
                disabled={uploadStatus || loading}
              >
                {myprofile ? "Update" : "Continue"}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    </Loader>
  );
};

export default UserOnboardingForm;
